import { useMemo, useState } from 'react'
import useThemes from '../../hooks/useThemes'
import ThemeRow from './ThemeRow'
import SectorFilter from './SectorFilter'
import { themeReadiness, compareByReadiness } from '../../helpers/intelligence/themeReadiness'
import { benchmarkStateByEffect } from '../../helpers/intelligence/effectBenchmarkGate'

/**
 * De themalijst: doelgroep × effect als enige ingang van de Intelligence-pagina.
 * Te filteren per sector, geordend op publiceerbaarheid.
 *
 * De drie toestanden blijven uit elkaar: fout, nog niet bekend, en echt leeg.
 * 'Er zijn geen thema's' is een bewering aan de lezer, en die is onwaar zolang
 * de gegevens onderweg zijn of de read geweigerd werd.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * Hier staan ÁLLE thema's, ook die zonder gepubliceerd inzicht — daar werk je
 * aan. De publieke API toont alleen thema's die al buiten staan.
 *
 * @param {object} props
 * @param {Array<object>} props.agendaRows - Alle agendarijen, voor de trede per thema
 * @param {Set<string>|null} props.publishedCellKeys - Cellen die al buiten staan; null = nog niet bekend
 * @param {Array<object>} props.publishedInsights - Gepubliceerde inzichten, voor trede en nalopen
 * @param {{loaded: boolean, rows: Array<object>}} [props.benchmarks] - De benchmarkrijen per effect; zonder dit telt elke benchmark als onbekend, en dan is geen thema publiceerbaar
 * @param {(id: string) => void} props.onOpen - Aangeroepen met de sleutel van het gekozen thema
 */
const ThemeList = ({ agendaRows, publishedCellKeys, publishedInsights, benchmarks = null, onOpen }) => {
    const { themes, loading, isEmpty, error } = useThemes()
    const [sector, setSector] = useState('')

    const sectoren = [...new Set(themes.flatMap((t) => t.sectors || []))].sort()

    // Trede per thema, één keer per wijziging van de invoer en niet per render:
    // de afleiding loopt over alle agendarijen per thema.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    // De tweede poort (ADR-040): haalt de benchmark van het effect de
    // klantdrempel? Zonder dat is 'Publiceerbaar' een belofte die de server
    // niet inlost.
    // AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js
    const benchmarkVoor = useMemo(() => benchmarkStateByEffect(benchmarks), [benchmarks])

    const gesorteerd = useMemo(() => themes
        .map((theme) => ({
            theme,
            ...themeReadiness({
                theme, agendaRows, publishedCellKeys, publishedInsights,
                benchmarkState: benchmarkVoor(theme.msiId),
            }),
        }))
        .sort(compareByReadiness), [themes, agendaRows, publishedCellKeys, publishedInsights, benchmarkVoor])

    const zichtbaar = sector
        ? gesorteerd.filter((item) => (item.theme.sectors || []).includes(sector))
        : gesorteerd

    const inhoud = () => {
        if (error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De thema&apos;s konden niet worden geladen: {error.message}
                </p>
            )
        }
        if (loading) return <p className="intelligence-state">Thema&apos;s laden…</p>
        if (isEmpty) {
            return (
                <p className="intelligence-state">
                    Er zijn nog geen thema&apos;s. Een thema ontstaat zodra een organisatie een
                    effect aan een doelgroep koppelt in haar Theory of Change.
                </p>
            )
        }
        // Wel thema's, maar niet in deze sector. Dat is iets anders dan 'geen
        // thema's', en zonder deze zin leest het als hetzelfde.
        if (!zichtbaar.length) {
            return (
                <p className="intelligence-state">
                    Geen thema&apos;s in deze sector. Kies &apos;Alle sectoren&apos; om ze allemaal te zien.
                </p>
            )
        }
        return (
            <div className="intelligence-theme-list">
                {zichtbaar.map((item) => (
                    <ThemeRow key={item.theme.id} theme={item.theme} readiness={item} onOpen={onOpen} />
                ))}
            </div>
        )
    }

    return (
        <div className="intelligence-panel">
            {sectoren.length > 1 && (
                <SectorFilter id="themas-sector" sectors={sectoren} value={sector} onChange={setSector} />
            )}
            {/* Zonder deze zin leest 'In aanloop' als een oordeel terwijl het
                'weet ik nog niet' is: de trede hangt óók aan de benchmark van
                het effect, en die komt per effect apart binnen.
                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
            {(!benchmarks || benchmarks.loaded !== true) && (
                <p className="intelligence-panel__hint">
                    De benchmarks van de effecten worden nog geladen; tot dat klaar is staat er bij
                    geen enkel thema &apos;Publiceerbaar&apos;.
                </p>
            )}
            {inhoud()}
        </div>
    )
}

export default ThemeList
